/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiFetchBaseQuery } from "@/redux/apiFetchBaseQuery";
import {
	DoohCoordinatesResponse,
	DoohScreenFetchPayload,
	DoohScreensCountResponse,
	DoohScreensResponse,
} from "@/types/DoohScreen";
import { createApi } from "@reduxjs/toolkit/query/react";

export const doohSlice = createApi({
	reducerPath: "doohSlice",
	baseQuery: apiFetchBaseQuery,
	tagTypes: ["Venues", "Regions", "Screens", "ScreenCoordinates", "audienceList"],
	endpoints: builder => ({
		getVenues: builder.query<any, void>({
			query: () => ({
				url: `/dooh/venues`,
			}),
			providesTags: ["Venues"],
		}),

		// Get and add regions by states
		getAndAddRegion: builder.mutation<any, { states: string[]; region_type: string }>({
			query: payload => {
				// Ensure the payload has the correct structure
				const { states, region_type } = payload;

				if (!states || !region_type) {
					throw new Error("Missing required fields: 'states' or 'region_type'.");
				}

				const formDataPayload = new FormData();
				const allStates = Array.isArray(states) ? states : [states];
				formDataPayload.append("region_type", region_type);
				allStates.forEach(state => formDataPayload.append("states[]", state));

				return {
					url: "/dooh/regions-by-states",
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
			invalidatesTags: ["Regions"], // Invalidate cache for regions
		}),

		addAudience: builder.mutation({
			query: formData => {
				// Check if the input is not already a FormData instance
				const formDataPayload = formData instanceof FormData ? formData : new FormData();

				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}

				return {
					url: "dooh/audiences",
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json", // Explicitly set accept header
					},
				};
			},
			invalidatesTags: ["audienceList"],
		}),
		// Get screen count
		getScreenCount: builder.mutation<DoohScreensCountResponse, DoohScreenFetchPayload>({
			query: formData => {
				const formDataPayload = formData instanceof FormData ? formData : new FormData();
				if (!(formData instanceof FormData)) {
					Object.entries(formData).forEach(([key, value]) => {
						formDataPayload.append(key, value as string | Blob);
					});
				}
				return {
					url: "dooh/screens/count",
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
		}),

		// Get and add screens
		getAndAddScreen: builder.mutation<DoohScreensResponse, { data: DoohScreenFetchPayload; page?: number }>({
			query: formData => {
				const formDataPayload = new FormData();
				Object.entries(formData.data).forEach(([key, value]) => {
					formDataPayload.append(key, value as string | Blob);
				});

				return {
					url: `dooh/screens?page_no=${formData.page}&limit=200`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
			invalidatesTags: ["Screens"],
		}),

		// Get and add screen coordinates
		getCoordinates: builder.mutation<DoohCoordinatesResponse, { data: Record<string, any> }>({
			query: formData => {
				const formDataPayload = new FormData();
				Object.entries(formData.data).forEach(([key, value]) => {
					formDataPayload.append(key, value as string | Blob);
				});

				return {
					url: `dooh/screens/coordinates?page_no=1&limit=150`,
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
			invalidatesTags: ["ScreenCoordinates"],
		}),

		// Get and add screens
		getPreflightReport: builder.mutation({
			query: formData => {
				const formDataPayload = new FormData();
				Object.entries(formData).forEach(([key, value]) => {
					formDataPayload.append(key, value as string | Blob);
				});

				return {
					url: `dooh/audiences/preflight-report/csv`,
					method: "POST",
					body: formDataPayload,
					responseHandler: response => response.blob(),
				};
			},
		}),

		getAudienceList: builder.query({
			query: ({ page, limit, search = "" }) => ({
				url: `dooh/audiences`,
				params: {
					page_no: page,
					limit,
					search,
				},
			}),
			providesTags: ["audienceList"],
		}),

		getAudienceDetail: builder.query({
			query: ({ id }) => ({
				url: `dooh/audiences/${id}`,
			}),
		}),

		deleteAudience: builder.mutation({
			query: ({ audienceId }: { audienceId: string }) => ({
				url: `dooh/audiences/${audienceId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["audienceList"],
		}),

		updateAudience: builder.mutation({
			query: ({ formData, audienceId }: any) => {
				const urlEncodedPayload = new URLSearchParams();

				Object.entries(formData).forEach(([key, value]) => {
					urlEncodedPayload.append(key, String(value));
				});

				return {
					url: `dooh/audiences/${audienceId}`,
					method: "PUT",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: urlEncodedPayload.toString(),
				};
			},
			invalidatesTags: ["audienceList"],
		}),
	}),
});

// Export hooks for usage in components
export const {
	useGetVenuesQuery,
	useGetAndAddRegionMutation,
	useGetAndAddScreenMutation,
	useAddAudienceMutation,
	useGetCoordinatesMutation,
	useGetScreenCountMutation,
	useGetPreflightReportMutation,
	useGetAudienceListQuery,
	useDeleteAudienceMutation,
	// useGetAudienceDetailQuery,
	useLazyGetAudienceDetailQuery,
	useUpdateAudienceMutation,
} = doohSlice;
